import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Scissors, Brush, Droplets, Hand, ArrowRight, MapPin, Clock, Phone, Loader2, Gift, ShoppingCart, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_latinbodyau/artifacts/fnru6dvp_WhatsApp%20Image%202026-01-20%20at%2012.18.12%20AM.jpeg";

const iconMap = {
  'Sparkles': Sparkles,
  'Scissors': Scissors,
  'Brush': Brush,
  'Droplets': Droplets,
  'Hand': Hand
};

export default function HomePage() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [socialMedia, setSocialMedia] = useState([]);
  const [settings, setSettings] = useState({ 
    heroVideoUrl: 'https://customer-assets.emergentagent.com/job_latinbodyau/artifacts/fmjd305e_WhatsApp%20Video%202026-01-20%20at%2012.19.05%20AM.mp4'
  });
  const [giftCardOptions, setGiftCardOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Product/Gift Card purchase dialog
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedGiftAmount, setSelectedGiftAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [buyerInfo, setBuyerInfo] = useState({ name: '', email: '', phone: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [servicesRes, productsRes, professionalsRes, testimonialsRes, galleryRes, settingsRes, giftCardOptionsRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/products`),
        axios.get(`${API}/professionals`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/gift-cards/options`)
      ]);
      setServices(servicesRes.data);
      setProducts(productsRes.data);
      setProfessionals(professionalsRes.data);
      setTestimonials(testimonialsRes.data);
      setGallery(galleryRes.data);
      setSettings(settingsRes.data);
      setSocialMedia(settingsRes.data.socialMedia || []);
      setGiftCardOptions(giftCardOptionsRes.data.filter(opt => opt.enabled));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const iconMapSocial = {
    'Instagram': 'https://cdn.simpleicons.org/instagram/white',
    'Facebook': 'https://cdn.simpleicons.org/facebook/white',
    'Twitter': 'https://cdn.simpleicons.org/twitter/white',
    'WhatsApp': 'https://cdn.simpleicons.org/whatsapp/white',
    'TikTok': 'https://cdn.simpleicons.org/tiktok/white'
  };

  // Format price in AUD
  const formatPrice = (price) => {
    return price.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
  };

  const handleProductPurchase = (product) => {
    setSelectedItem({ type: 'product', data: product });
    setBuyerInfo({ name: '', email: '', phone: '' });
    setPurchaseDialogOpen(true);
  };

  const handleGiftCardPurchase = () => {
    setSelectedItem({ type: 'giftcard' });
    setSelectedGiftAmount(null);
    setCustomAmount('');
    setBuyerInfo({ name: '', email: '', phone: '' });
    setPurchaseDialogOpen(true);
  };

  const handlePurchaseSubmit = async () => {
    try {
      if (!buyerInfo.name || !buyerInfo.email || !buyerInfo.phone) {
        toast.error('Please fill all fields');
        return;
      }

      if (selectedItem.type === 'giftcard') {
        // Gift card purchase
        let amount = 0;
        if (selectedGiftAmount?.isCustom) {
          amount = parseInt(customAmount);
          if (!amount || amount < 10) {
            toast.error('Please enter a valid amount (minimum $10)');
            return;
          }
        } else if (selectedGiftAmount) {
          amount = selectedGiftAmount.amount;
        } else {
          toast.error('Please select an amount');
          return;
        }

        // Create gift card purchase
        const purchaseRes = await axios.post(`${API}/gift-cards/purchase`, {
          amount,
          buyerName: buyerInfo.name,
          buyerEmail: buyerInfo.email,
          buyerPhone: buyerInfo.phone
        });

        // Create payment
        const paymentRes = await axios.post(`${API}/gift-cards/payment`, {
          gift_card_id: purchaseRes.data.id,
          origin_url: window.location.origin
        });

        // Redirect to Stripe
        window.location.href = paymentRes.data.url;
      } else {
        // Product purchase
        const paymentRes = await axios.post(`${API}/payments/create-product-checkout`, {
          product_id: selectedItem.data.id,
          origin_url: window.location.origin,
          buyer_info: buyerInfo
        });

        // Redirect to Stripe
        window.location.href = paymentRes.data.url;
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Error processing purchase');
    }
  };

  // Format center hours for footer
  const formatCenterHours = () => {
    if (!settings.centerHours) return 'Mon-Fri: 9:00-20:00 | Sat: 9:00-15:00';
    
    const hours = settings.centerHours;
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const saturday = hours.saturday;
    
    // Check if all weekdays have same hours
    const weekdayHours = weekdays.filter(day => hours[day]?.enabled);
    if (weekdayHours.length > 0) {
      const firstDay = hours[weekdayHours[0]];
      const allSame = weekdayHours.every(day => 
        hours[day].start === firstDay.start && hours[day].end === firstDay.end
      );
      
      if (allSame && saturday?.enabled) {
        return `Mon-Fri: ${firstDay.start}-${firstDay.end} | Sat: ${saturday.start}-${saturday.end}`;
      }
    }
    
    return 'Mon-Fri: 9:00-20:00 | Sat: 9:00-15:00';
  };

  // Loading Screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex flex-col items-center justify-center" data-testid="loading-screen">
        <img src={LOGO_URL} alt="Latin Body" className="h-24 w-24 rounded-full object-cover mb-6 shadow-lg" />
        <Loader2 className="h-8 w-8 animate-spin text-black mb-4" />
        <p className="text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Header */}
      <header className="bg-white border-b border-[#e8dcc8] sticky top-0 z-50" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')} data-testid="brand-logo">
            <img 
              src={LOGO_URL} 
              alt="Latin Body" 
              className="h-20 w-auto object-contain"
              style={{ maxHeight: '80px' }}
            />
          </div>
          <nav className="flex gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} data-testid="nav-home-btn">Home</Button>
            <Button 
              className="bg-black text-[#e8dcc8] hover:bg-black/90" 
              onClick={() => navigate('/reservar')}
              data-testid="nav-booking-btn"
            >
              Book Now
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section with Video */}
      <section className="relative h-[85vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-black">
          <video 
            src={settings.heroVideoUrl || "https://customer-assets.emergentagent.com/job_latinbodyau/artifacts/fmjd305e_WhatsApp%20Video%202026-01-20%20at%2012.19.05%20AM.mp4"}
            className="absolute top-1/2 left-1/2"
            style={{ 
              transform: 'translate(-50%, -50%) rotate(-90deg) scale(1.5)',
              transformOrigin: 'center center'
            }}
            autoPlay
            muted
            loop
            playsInline
            data-testid="hero-video"
          />
          <div className="absolute inset-0 bg-black/20" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold elegant-script mb-6" data-testid="hero-title">
            Where Heat, Water & Hands Transform Your Body
          </h2>
          <p className="text-lg sm:text-xl mb-8 max-w-2xl mx-auto">
            Warm water, healing heat, expert touch and sculpting treatments come together in a luxurious and welcoming space designed for deep relaxation and visible body results.
          </p>
          <Button 
            size="lg" 
            className="bg-[#e8dcc8] text-black hover:bg-[#e8dcc8]/90 text-lg px-8 py-6"
            onClick={() => navigate('/reservar')}
            data-testid="hero-cta-btn"
          >
            Book Now <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 max-w-7xl mx-auto" data-testid="services-section">
        <h2 className="text-3xl sm:text-4xl font-bold elegant-script text-center mb-12">Our Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon] || Sparkles;
            const hasDurations = service.durations && service.durations.length > 0;
            const minDur = hasDurations ? Math.min(...service.durations.map(d => d.duration)) : service.duration;
            const maxDur = hasDurations ? Math.max(...service.durations.map(d => d.duration)) : service.duration;
            const minPrice = hasDurations ? Math.min(...service.durations.map(d => d.price)) : service.price;
            const maxPrice = hasDurations ? Math.max(...service.durations.map(d => d.price)) : service.price;
            
            return (
              <Card key={service.id} className="card-hover border-[#e8dcc8]" data-testid={`service-card-${service.id}`}>
                <CardContent className="p-6">
                  <div className="mb-4">
                    <IconComponent className="w-12 h-12 text-black" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-semibold">
                      {minDur === maxDur ? `${minDur} min` : `${minDur}-${maxDur} min`}
                    </span>
                    <span className="text-lg font-bold">
                      {minPrice === maxPrice ? formatPrice(minPrice) : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 px-4 bg-white" data-testid="products-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold elegant-script text-center mb-12">Our Products</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Gift Cards */}
            {giftCardOptions.filter(opt => !opt.isCustom).map((giftCard) => (
              <Card key={giftCard.id} className="card-hover border-[#e8dcc8]" data-testid={`gift-card-${giftCard.id}`}>
                <CardContent className="p-0">
                  <div className="relative">
                    <img 
                      src={giftCard.imageUrl || "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=400&h=300&fit=crop"} 
                      alt={`${formatPrice(giftCard.amount)} Gift Card`} 
                      className="w-full h-48 object-cover" 
                    />
                    <div className="absolute top-2 right-2 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      <Gift className="w-3 h-3" />
                      Gift Card
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold mb-2">{formatPrice(giftCard.amount)} Gift Card</h3>
                    <p className="text-sm text-gray-600 mb-3">Give the gift of beauty and relaxation</p>
                    <p className="text-lg font-bold mb-3">{formatPrice(giftCard.amount)}</p>
                    <Button 
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                      onClick={handleGiftCardPurchase}
                      data-testid={`buy-gift-card-${giftCard.id}`}
                    >
                      <ShoppingCart className="mr-2 h-4 w-4" /> Purchase Gift Card
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Custom Amount Gift Card - if enabled */}
            {giftCardOptions.some(opt => opt.isCustom) && (
              <Card className="card-hover border-[#e8dcc8]" data-testid="gift-card-custom">
                <CardContent className="p-0">
                  <div className="relative">
                    <img 
                      src={giftCardOptions.find(opt => opt.isCustom)?.imageUrl || "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=400&h=300&fit=crop"} 
                      alt="Custom Amount Gift Card" 
                      className="w-full h-48 object-cover" 
                    />
                    <div className="absolute top-2 right-2 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      <Gift className="w-3 h-3" />
                      Gift Card
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold mb-2">Custom Amount Gift Card</h3>
                    <p className="text-sm text-gray-600 mb-3">Choose your own amount</p>
                    <p className="text-lg font-bold mb-3">From $10+</p>
                    <Button 
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                      onClick={handleGiftCardPurchase}
                      data-testid="buy-gift-card-custom"
                    >
                      <ShoppingCart className="mr-2 h-4 w-4" /> Purchase Gift Card
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Regular Products */}
            {products.map((product, idx) => (
              <Card key={idx} className="card-hover border-[#e8dcc8]" data-testid={`product-card-${idx}`}>
                <CardContent className="p-0">
                  <img src={product.imageUrl} alt={product.name} className="w-full h-48 object-cover" />
                  <div className="p-4">
                    <h3 className="font-bold mb-2">{product.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{product.description}</p>
                    <p className="text-lg font-bold mb-3">{formatPrice(product.price)}</p>
                    <Button 
                      className="w-full bg-black text-[#e8dcc8] hover:bg-black/90"
                      onClick={() => handleProductPurchase(product)}
                      data-testid={`buy-product-${idx}`}
                    >
                      <ShoppingCart className="mr-2 h-4 w-4" /> Buy Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Professionals Section */}
      <section className="py-20 px-4 max-w-7xl mx-auto" data-testid="professionals-section">
        <h2 className="text-3xl sm:text-4xl font-bold elegant-script text-center mb-12">Meet Our Professionals</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {professionals.map((prof) => (
            <Card key={prof.id} className="card-hover border-[#e8dcc8] text-center" data-testid={`professional-card-${prof.id}`}>
              <CardContent className="p-6">
                <img src={prof.imageUrl} alt={prof.name} className="w-32 h-32 rounded-full mx-auto mb-4 object-cover" />
                <h3 className="text-xl font-bold mb-2">{prof.name}</h3>
                <p className="text-gray-600">{prof.specialty}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 px-4 bg-white" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold elegant-script text-center mb-12">Our Gallery</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {gallery.map((item, idx) => (
              <div key={item.id || idx} className="aspect-square overflow-hidden rounded-lg" data-testid={`gallery-img-${idx}`}>
                {item.type === 'video' ? (
                  <video 
                    src={item.imageUrl} 
                    className="w-full h-full object-cover" 
                    controls 
                    playsInline
                  />
                ) : (
                  <img src={item.imageUrl} alt={item.description || `Gallery ${idx + 1}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-300" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 max-w-7xl mx-auto" data-testid="testimonials-section">
        <h2 className="text-3xl sm:text-4xl font-bold elegant-script text-center mb-12">What Our Clients Say</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, idx) => (
            <Card key={testimonial.id || idx} className={`border-[#e8dcc8] ${testimonial.isFixed ? 'bg-amber-50 border-amber-200' : ''}`} data-testid={`testimonial-card-${idx}`}>
              <CardContent className="p-6">
                {testimonial.isFixed && (
                  <div className="flex items-center gap-2 mb-3">
                    <img src="https://cdn.simpleicons.org/google" alt="Google" className="w-5 h-5" />
                    <span className="text-xs text-amber-700 font-semibold">Google Maps Review</span>
                  </div>
                )}
                <p className="text-gray-700 italic mb-4">"{testimonial.quote}"</p>
                <p className="font-bold">- {testimonial.author}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-[#e8dcc8] py-12 px-4" data-testid="footer">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="Latin Body" className="h-12 w-12 rounded-full object-cover" />
              <h3 className="text-xl font-bold elegant-script">Latin Body</h3>
            </div>
            <p className="text-sm">Beauty center specialized in facial and body treatments</p>
            {socialMedia.length > 0 && (
              <div className="flex gap-3 mt-4">
                {socialMedia.map((social) => (
                  <a 
                    key={social.id} 
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                    data-testid={`social-link-${social.platform.toLowerCase()}`}
                  >
                    <img src={iconMapSocial[social.icon] || iconMapSocial['Instagram']} alt={social.platform} className="w-6 h-6" />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div>
            <h4 className="font-bold mb-4">Contact</h4>
            <div className="space-y-2 text-sm">
              <a 
                href="https://maps.google.com/?q=73+Murrumbeena+Rd,+Murrumbeena+VIC+3163,+Australia" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-white transition-colors"
                data-testid="address-link"
              >
                <MapPin size={16} /> 73 Murrumbeena Rd, Murrumbeena VIC 3163, Australia
              </a>
              <p className="flex items-center gap-2"><Phone size={16} /> +61 416 703 912</p>
              <p className="flex items-center gap-2"><Clock size={16} /> {formatCenterHours()}</p>
            </div>
          </div>
          <div>
            <h4 className="font-bold mb-4">Administration</h4>
            <Button 
              variant="outline" 
              className="border-[#e8dcc8] text-[#e8dcc8] hover:bg-[#e8dcc8] hover:text-black"
              onClick={() => navigate('/admin')}
              data-testid="footer-admin-btn"
            >
              Access Panel
            </Button>
            <div className="mt-4">
              <Button 
                variant="link" 
                className="text-[#e8dcc8] p-0 h-auto"
                onClick={() => navigate('/consultar-turno')}
                data-testid="footer-check-booking-btn"
              >
                Check my booking
              </Button>
            </div>
          </div>
        </div>
      </footer>

      {/* Purchase Dialog */}
      <Dialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedItem?.type === 'giftcard' ? 'Purchase Gift Card' : `Purchase ${selectedItem?.data?.name}`}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedItem?.type === 'giftcard' && (
              <div>
                <Label>Select Amount</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {giftCardOptions.map((option) => (
                    <Button
                      key={option.id}
                      variant={selectedGiftAmount?.id === option.id ? 'default' : 'outline'}
                      className={selectedGiftAmount?.id === option.id ? 'bg-amber-500 hover:bg-amber-600' : ''}
                      onClick={() => setSelectedGiftAmount(option)}
                    >
                      {option.isCustom ? 'Custom Amount' : formatPrice(option.amount)}
                    </Button>
                  ))}
                </div>
                
                {selectedGiftAmount?.isCustom && (
                  <div className="mt-3">
                    <Label htmlFor="custom-amount">Enter Amount (AUD)</Label>
                    <Input
                      id="custom-amount"
                      type="number"
                      min="10"
                      placeholder="Enter amount"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            )}

            {selectedItem?.type === 'product' && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-semibold">{selectedItem.data.name}</p>
                <p className="text-sm text-gray-600">{selectedItem.data.description}</p>
                <p className="font-bold text-lg mt-2">{formatPrice(selectedItem.data.price)}</p>
              </div>
            )}

            <div>
              <Label htmlFor="buyer-name">Full Name *</Label>
              <Input
                id="buyer-name"
                placeholder="Your full name"
                value={buyerInfo.name}
                onChange={(e) => setBuyerInfo({ ...buyerInfo, name: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="buyer-email">Email *</Label>
              <Input
                id="buyer-email"
                type="email"
                placeholder="your@email.com"
                value={buyerInfo.email}
                onChange={(e) => setBuyerInfo({ ...buyerInfo, email: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="buyer-phone">Phone Number *</Label>
              <Input
                id="buyer-phone"
                type="tel"
                placeholder="+61 XXX XXX XXX"
                value={buyerInfo.phone}
                onChange={(e) => setBuyerInfo({ ...buyerInfo, phone: e.target.value })}
                className="mt-1"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setPurchaseDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-black text-[#e8dcc8] hover:bg-black/90"
                onClick={handlePurchaseSubmit}
              >
                Proceed to Payment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
